package com.rainbowclubs.rainbow.presentation.home

import androidx.compose.animation.core.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.rainbowclubs.rainbow.data.models.AttendeeStatus
import com.rainbowclubs.rainbow.data.models.HomeResponse
import com.rainbowclubs.rainbow.data.models.MembershipStatus
import com.rainbowclubs.rainbow.data.models.UserRole
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.common.MembershipStatusCard
import com.rainbowclubs.rainbow.presentation.common.ads.AdBanner
import com.rainbowclubs.rainbow.presentation.common.bottomNavBar.BottomNavBar
import com.rainbowclubs.rainbow.presentation.common.bottomNavBar.BottomNavItem
import com.rainbowclubs.rainbow.presentation.theme.*
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun HomeScreen(
    clubId: String,
    viewModel: HomeViewModel = koinViewModel(parameters = { parametersOf(clubId) }),
    navigateTo: (HomeViewModel.HomeNavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()
    val rsvpLoadingState by viewModel.rsvpLoadingState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
        bottomBar = {
            val shouldShowBannerAd =
                (screenState as? ScreenState.Content)?.data?.membership?.status == MembershipStatus.APPROVED

            Column {
                if (shouldShowBannerAd) {
                    AdBanner()
                }

                BottomNavBar(
                    selectedItem = BottomNavItem.Home,
                    onHomeClick = { },
                    onClubClick = { viewModel.navigateToClub() },
                    onMessagesClick = { viewModel.navigateToMessages() },
                    onMoreClick = { viewModel.navigateToMore() },
                )
            }
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier.padding(paddingValues).fillMaxSize(),

            ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = { viewModel.fetchHomeData() },
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> {
                    val membershipStatus = state.data.membership?.status
                    if (membershipStatus == MembershipStatus.APPROVED) {
                        HomeScreenContent(
                            data = state.data,
                            onAdminClick = { viewModel.navigateToAdmin() },
                            onRsvpClick = { status, eventId ->
                                viewModel.updateRsvp(
                                    status,
                                    eventId
                                )
                            },
                            onEventClick = { viewModel.navigateToEventDetails(it) },
                            formatDate = { viewModel.formatDate(it) },
                            formatTime = { viewModel.formatTime(it) },
                            rsvpLoadingState = rsvpLoadingState,
                        )
                    } else {
                        MembershipStatusCard(
                            status = membershipStatus ?: MembershipStatus.UNKNOWN,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun HomeScreenContent(
    data: HomeResponse,
    onAdminClick: () -> Unit,
    onRsvpClick: (AttendeeStatus, String) -> Unit,
    onEventClick: (String) -> Unit,
    formatDate: (Long) -> String,
    formatTime: (Long) -> String,
    rsvpLoadingState: Pair<String, AttendeeStatus>?,
) {
    val isAdmin =
        data.membership?.role == UserRole.ADMIN || data.membership?.role == UserRole.SUPER_ADMIN

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        Spacer(modifier = Modifier.height(24.dp))

        Text(
            text = stringResource(Res.string.welcome_back, data.user.name ?: ""),
            fontSize = 16.sp,
            color = MaterialTheme.colorScheme.outline,
            maxLines = 1,
        )
        Text(
            text = stringResource(Res.string.clubs_overview),
            fontSize = 28.sp,
            fontWeight = FontWeight.Bold,
            lineHeight = 34.sp,
            color = MaterialTheme.colorScheme.onBackground,
        )

        Spacer(modifier = Modifier.height(24.dp))

        if (isAdmin) {
            AdminBanner(onAdminClick)
            Spacer(modifier = Modifier.height(24.dp))
        }

        data.ongoingEvent?.let { event ->
            val currentUserStatus = event.attendees.find { it.userId == data.user.id }?.status
            val loadingStatus =
                if (rsvpLoadingState?.first == event.id) rsvpLoadingState?.second else null
            EventSection(
                title = stringResource(Res.string.now),
                isLive = true,
                event = event,
                currentUserStatus = currentUserStatus,
                onStatusClick = { onRsvpClick(it, event.id!!) },
                onEventClick = { event.id?.let(onEventClick) },
                formatDate = formatDate,
                formatTime = formatTime,
                loadingStatus = loadingStatus,
            )
            Spacer(modifier = Modifier.height(24.dp))
        }

        data.upcomingEvent?.let { event ->
            val currentUserStatus = event.attendees.find { it.userId == data.user.id }?.status
            val loadingStatus =
                if (rsvpLoadingState?.first == event.id) rsvpLoadingState?.second else null
            EventSection(
                title = stringResource(Res.string.upcoming_event),
                event = event,
                currentUserStatus = currentUserStatus,
                onStatusClick = { onRsvpClick(it, event.id!!) },
                onEventClick = { event.id?.let(onEventClick) },
                formatDate = formatDate,
                formatTime = formatTime,
                loadingStatus = loadingStatus,
            )
        }

        if (data.ongoingEvent == null && data.upcomingEvent == null) {
            NoEventsCard()
        }

        Spacer(modifier = Modifier.height(16.dp))
    }
}

@Composable
private fun NoEventsCard() {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 16.dp),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
        ),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.3f))
    ) {
        Column(
            modifier = Modifier
                .padding(24.dp)
                .fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_calendar),
                contentDescription = null,
                modifier = Modifier.size(48.dp),
                tint = MaterialTheme.colorScheme.primary.copy(alpha = 0.6f)
            )
            Spacer(modifier = Modifier.height(16.dp))
            Text(
                text = stringResource(Res.string.no_upcoming_events_title),
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface,
                textAlign = TextAlign.Center
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = stringResource(Res.string.no_upcoming_events_description),
                fontSize = 14.sp,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
                lineHeight = 20.sp
            )
        }
    }
}

@Composable
private fun AdminBanner(onClick: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border = BorderStroke(
            1.dp,
            MaterialTheme.colorScheme.outlineVariant
        )
    ) {
        Row(
            modifier = Modifier
                .padding(16.dp)
                .fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .background(
                        color = MaterialTheme.colorScheme.outlineVariant,
                        shape = CircleShape,
                    ),
                contentAlignment = Alignment.Center,
            ) {
                Box(
                    modifier = Modifier
                        .size(24.dp),
                ) {
                    Icon(
                        modifier = Modifier.size(24.dp),
                        painter = painterResource(Res.drawable.ic_admin),
                        contentDescription = stringResource(Res.string.admin_icon_cd),
                    )
                }

            }

            Spacer(modifier = Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.you_are_admin),
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary,
                    fontSize = 16.sp,
                )
                Text(
                    text = stringResource(Res.string.admin_banner_description),
                    color = MaterialTheme.colorScheme.outline,
                    fontSize = 14.sp
                )

                OutlinedButton(
                    onClick = onClick,
                    shape = RoundedCornerShape(8.dp),
                    border = BorderStroke(
                        width = 1.dp,
                        color = MaterialTheme.colorScheme.primary,
                    ),
                    contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                ) {
                    Text(
                        stringResource(Res.string.go_to_admin),
                        color = MaterialTheme.colorScheme.primary,
                        fontSize = 14.sp,
                    )
                    Spacer(modifier = Modifier.width(4.dp))
                    Icon(
                        painter = painterResource(Res.drawable.ic_chevron_right),
                        contentDescription = stringResource(Res.string.chevron_icon_cd),
                        modifier = Modifier.size(16.dp),
                        tint = MaterialTheme.colorScheme.primary,
                    )
                }
            }
        }
    }
}

@Composable
private fun BlinkingRedDot(modifier: Modifier = Modifier) {
    val infiniteTransition = rememberInfiniteTransition()
    val alpha by infiniteTransition.animateFloat(
        initialValue = 1f,
        targetValue = 0.2f,
        animationSpec = infiniteRepeatable(
            animation = tween(1000, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse
        )
    )

    Box(
        modifier = modifier
            .size(10.dp)
            .graphicsLayer(alpha = alpha)
            .background(color = MaterialTheme.colorScheme.rsvpNotGoing, shape = CircleShape)
    )
}

@Composable
private fun EventSection(
    title: String,
    event: com.rainbowclubs.rainbow.data.models.Event,
    currentUserStatus: AttendeeStatus?,
    onStatusClick: (AttendeeStatus) -> Unit,
    onEventClick: () -> Unit,
    formatDate: (Long) -> String,
    formatTime: (Long) -> String,
    isLive: Boolean = false,
    loadingStatus: AttendeeStatus? = null,
) {

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.colorScheme.surface,
                shape = RoundedCornerShape(16.dp)
            )
            .border(
                2.dp,
                MaterialTheme.colorScheme.rainbowGradient,
                RoundedCornerShape(16.dp),
            )
            .clickable(onClick = onEventClick)
            .padding(16.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (isLive) {
                BlinkingRedDot()
            } else {
                Icon(
                    painter = painterResource(Res.drawable.ic_calendar),
                    contentDescription = stringResource(Res.string.calendar_icon_cd),
                    modifier = Modifier.size(20.dp),
                    tint = MaterialTheme.colorScheme.primary,
                )
            }
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = title,
                color = MaterialTheme.colorScheme.primary,
                fontWeight = FontWeight.Bold,
                fontSize = 14.sp
            )
        }

        Spacer(modifier = Modifier.height(16.dp))

        Row(modifier = Modifier.fillMaxWidth()) {
            // Event Image Placeholder
//            Box(
//                modifier = Modifier
//                    .size(width = 140.dp, height = 200.dp)
//                    .clip(RoundedCornerShape(12.dp))
//                    .background(Color.LightGray)
//            ) {
//                Text(
//                    "Event Image",
//                    modifier = Modifier.align(Alignment.Center),
//                    fontSize = 10.sp,
//                    color = Color.DarkGray
//                )
//            }
//
//            Spacer(modifier = Modifier.width(16.dp))

            Column {
                Text(
                    text = event.title,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = MaterialTheme.colorScheme.onSurface,
                    lineHeight = 22.sp,
                )

                Spacer(modifier = Modifier.height(8.dp))

                Text(
                    text = event.description,
                    fontSize = 14.sp,
                    color = MaterialTheme.colorScheme.outline,
                    lineHeight = 18.sp,
                    maxLines = 3,
                    overflow = TextOverflow.Ellipsis,

                )

                Spacer(modifier = Modifier.height(16.dp))

                EventInfoRow(
                    text = formatDate(event.startsAt),
                    drawableResource = Res.drawable.ic_calendar,
                )
                EventInfoRow(
                    text = "${formatTime(event.startsAt)} - ${formatTime(event.endsAt)}",
                    drawableResource = Res.drawable.ic_clock,
                )
                event.location?.let {
                    EventInfoRow(
                        text = it,
                        drawableResource = Res.drawable.ic_location,
                    )
                }
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            RsvpButton(
                text = stringResource(Res.string.going),
                drawableResource = Res.drawable.ic_check,
                status = AttendeeStatus.YES,
                isSelected = (currentUserStatus == AttendeeStatus.YES) || (loadingStatus == AttendeeStatus.YES),
                isLoading = loadingStatus == AttendeeStatus.YES,
                onClick = { onStatusClick(AttendeeStatus.YES) },
                modifier = Modifier.weight(1f)
            )
            RsvpButton(
                text = stringResource(Res.string.maybe),
                drawableResource = Res.drawable.ic_question_mark,
                status = AttendeeStatus.MAYBE,
                isSelected = (currentUserStatus == AttendeeStatus.MAYBE) || (loadingStatus == AttendeeStatus.MAYBE),
                isLoading = loadingStatus == AttendeeStatus.MAYBE,
                onClick = { onStatusClick(AttendeeStatus.MAYBE) },
                modifier = Modifier.weight(1f)
            )
            RsvpButton(
                text = stringResource(Res.string.not_going),
                drawableResource = Res.drawable.ic_cancel,
                status = AttendeeStatus.NO,
                isSelected = (currentUserStatus == AttendeeStatus.NO) || (loadingStatus == AttendeeStatus.NO),
                isLoading = loadingStatus == AttendeeStatus.NO,
                onClick = { onStatusClick(AttendeeStatus.NO) },
                modifier = Modifier.weight(1f),
            )
        }
    }
}

@Composable
private fun EventInfoRow(
    text: String,
    drawableResource: DrawableResource,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.padding(vertical = 2.dp),
    ) {
        Box(
            modifier = Modifier
                .size(16.dp)
        ) {
            Icon(
                modifier = Modifier.size(16.dp),
                painter = painterResource(drawableResource),
                contentDescription = text,
            )
        }
        Spacer(modifier = Modifier.width(8.dp))
        Text(
            text = text,
            fontSize = 13.sp,
            color = MaterialTheme.colorScheme.outline,
        )
    }
}

@Composable
private fun RsvpButton(
    text: String,
    drawableResource: DrawableResource,
    status: AttendeeStatus,
    isSelected: Boolean,
    isLoading: Boolean = false,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val colorScheme = MaterialTheme.colorScheme
    val (color, backgroundColor) = when (status) {
        AttendeeStatus.YES -> colorScheme.rsvpGoing to colorScheme.rsvpGoingContainer
        AttendeeStatus.MAYBE -> colorScheme.rsvpMaybe to colorScheme.rsvpMaybeContainer
        AttendeeStatus.NO -> colorScheme.rsvpNotGoing to colorScheme.rsvpNotGoingContainer
    }

    val finalBackgroundColor = if (isSelected) backgroundColor else colorScheme.surface
    val finalContentColor = if (isSelected) color else colorScheme.outline
    val finalBorderColor = if (isSelected) color.copy(alpha = 0.2f) else colorScheme.outlineVariant

    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(8.dp),
        color = finalBackgroundColor,
        border = BorderStroke(1.dp, finalBorderColor),
        modifier = modifier,
        enabled = !isLoading,
    ) {
        Row(
            modifier = Modifier.padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center
        ) {
            if (isLoading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(18.dp),
                    color = finalContentColor,
                    strokeWidth = 2.dp,
                )
            } else {
                Icon(
                    painter = painterResource(drawableResource),
                    contentDescription = text,
                    modifier = Modifier.size(18.dp),
                    tint = finalContentColor,
                )
            }
            Spacer(modifier = Modifier.width(4.dp))
            Text(
                maxLines = 1,
                text = text,
                color = finalContentColor,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Preview
@Composable
private fun NoEventsPreview() {
    MaterialTheme {
        Surface {
            Box(modifier = Modifier.padding(16.dp)) {
                NoEventsCard()
            }
        }
    }
}

@Preview
@Composable
private fun AdminBannerPreview() {
    MaterialTheme {
        Box(modifier = Modifier.padding(16.dp)) {
            AdminBanner(onClick = {})
        }
    }
}

@Preview
@Composable
private fun UpcomingEventSectionPreview() {
    val mockEvent = com.rainbowclubs.rainbow.data.models.Event(
        id = null,
        clubId = "",
        title = "Pride Parade Volunteering 🌈",
        description = "Join us as we spread love and support at the annual Pride Parade. Let's make a difference together!\n second line",
        startsAt = 1716624000000L,
        endsAt = 1716638400000L,
        location = "Central Park, New York",
        comments = emptyList(),
        attendees = emptyList(),
    )
    MaterialTheme {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            EventSection(
                title = "NOW",
                isLive = true,
                event = mockEvent,
                currentUserStatus = AttendeeStatus.YES,
                onStatusClick = {},
                onEventClick = {},
                formatDate = { "Sat, May 25 2024" },
                formatTime = { "10:00 AM" },
            )

            EventSection(
                title = "UPCOMING EVENT",
                event = mockEvent,
                currentUserStatus = AttendeeStatus.YES,
                onStatusClick = {},
                onEventClick = {},
                formatDate = { "Sat, May 25 2024" },
                formatTime = { "10:00 AM" },
            )

            EventSection(
                title = "LOADING RSVP",
                event = mockEvent,
                currentUserStatus = null,
                onStatusClick = {},
                onEventClick = {},
                formatDate = { "Sat, May 25 2024" },
                formatTime = { "10:00 AM" },
                loadingStatus = AttendeeStatus.YES,
            )
        }
    }
}

@Preview
@Composable
private fun RsvpButtonPreview() {
    MaterialTheme {
        Row(
            modifier = Modifier.padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            RsvpButton(
                text = "Going",
                status = AttendeeStatus.YES,
                isSelected = true,
                onClick = {},
                drawableResource = Res.drawable.ic_check,
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Preview
@Composable
private fun MembershipStatusCardPendingPreview() {
    MaterialTheme {
        MembershipStatusCard(
            status = MembershipStatus.PENDING,
        )
    }
}

@Preview
@Composable
private fun MembershipStatusCardBlockedPreview() {
    MaterialTheme {
        MembershipStatusCard(
            status = MembershipStatus.BLOCKED,
        )
    }
}
